import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Lock, ShieldCheck } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CrisisLines } from "@/components/site/CrisisLines";
import { Services } from "@/components/site/Services";
import { SafeHousing } from "@/components/site/SafeHousing";
import { DVSupport } from "@/components/site/DVSupport";
import { SafeTransport } from "@/components/site/SafeTransport";
import { SafetyCheckIn } from "@/components/site/SafetyCheckIn";

const SERVICE_OPTS = ["General support", "Domestic violence", "Safe housing", "School transport", "Family contact & appointments", "Mental health", "Counselling", "Food hamper", "Art therapy"];
const PLACEHOLDERS = {
  "School transport": "Child's name/age, school, days and pick-up area. Share only what you're comfortable with.",
  "Family contact & appointments": "Who is travelling, where to (family visit or appointment), date and time. Share only what you're comfortable with.",
};
const empty = { name: "", service: "General support", contact: "", contact_method: "Phone", safe_to_contact: "", safe_times: [], code_word: "", ok_to_leave_message: "", message: "" };

const SupportForm = ({ service, setService }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/support", { ...form, service });
      setDone(true);
      setForm(empty);
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="support-success" className="rounded-3xl bg-gum p-10 text-sand">
        <ShieldCheck className="h-10 w-10" />
        <p className="mt-6 font-display text-4xl">Your request is safe with us.</p>
        <p className="mt-3 text-sand/85">A member of our team will reach out the way you asked. If you're in danger, call 000.</p>
        <button data-testid="support-again-button" onClick={() => setDone(false)} className="mt-8 text-sm underline underline-offset-4">Send another request</button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} id="support-form" data-testid="support-form" className="scroll-mt-28 rounded-3xl bg-sand p-8 sm:p-10">
      <p className="flex items-center gap-2 text-sm text-ink/80"><Lock className="h-4 w-4 text-ochre" /> Confidential — only GAC support staff can see this.</p>
      <p className="eyebrow mt-6 text-ink/70">I'd like help with</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {SERVICE_OPTS.map((s) => (
          <button type="button" key={s} data-testid={`support-service-${s.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setService(s)}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${service === s ? "border-gum bg-gum text-sand" : "border-ink/25 hover:border-ink"}`}>{s}</button>
        ))}
      </div>
      <input data-testid="support-name-input" placeholder="First name or nickname (optional)" value={form.name} onChange={set("name")} className="field mt-6" />
      <div className="mt-6 flex flex-wrap gap-2">
        {["Phone", "Text", "Email"].map((m) => (
          <button type="button" key={m} data-testid={`support-method-${m.toLowerCase()}`} onClick={() => setForm({ ...form, contact_method: m })}
            className={`rounded-full border px-4 py-2 text-sm transition-colors ${form.contact_method === m ? "border-ochre bg-ochre text-sand" : "border-ink/25 hover:border-ink"}`}>{m}</button>
        ))}
      </div>
      <input data-testid="support-contact-input" required placeholder={form.contact_method === "Email" ? "Email address" : "Phone number"} value={form.contact} onChange={set("contact")} className="field mt-4" />
      <SafetyCheckIn form={form} setForm={setForm} />
      <textarea data-testid="support-message-input" required rows={4} placeholder={PLACEHOLDERS[service] || "What support would help? Share only what you're comfortable with."} value={form.message} onChange={set("message")} className="field mt-4 resize-none" />
      <button data-testid="support-submit-button" disabled={busy} className="btn-ochre mt-8 w-full justify-center disabled:opacity-60">{busy ? "Sending…" : "Request support privately"}</button>
    </form>
  );
};

export default function SafePlace() {
  const [service, setService] = useState("General support");
  const request = (s) => {
    setService(s);
    document.getElementById("support-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <main>
      <PageHero eyebrow="Safe Place" lines={["A place to", <span key="b" className="italic text-ochre">belong.</span>]}
        intro="A culturally safe space for community to drop in, yarn and get support — family and domestic violence support, safe housing for women and children, safe transport to school, family and appointments, mental health, counselling, food hampers and art therapy, with no judgement and no pressure." image="/img/headland.jpg" />
      <section data-testid="services-section" className="bg-sand px-5 pt-20 sm:px-10">
        <div className="mx-auto max-w-[1440px]">
          <Reveal className="mb-10">
            <p className="eyebrow text-ochre">Support services</p>
            <h2 className="mt-4 font-display text-4xl font-medium tracking-tight sm:text-5xl">How we can help</h2>
          </Reveal>
          <Services onRequest={request} />
        </div>
      </section>
      <DVSupport onRequest={request} />
      <SafeHousing onRequest={request} />
      <SafeTransport onRequest={request} />
      <section className="bg-sand px-5 py-20 sm:px-10">
        <div className="mx-auto max-w-[1440px]">
          <Reveal>
            <p className="eyebrow text-ochre">Need help now?</p>
            <h2 className="mt-4 font-display text-4xl font-medium tracking-tight sm:text-5xl">Support lines</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10"><CrisisLines /></Reveal>
          <p className="mt-6 text-sm text-ink/75">Need to leave quickly? Press <span className="font-mono">Esc</span> or tap Quick exit at any time.</p>
        </div>
      </section>
      <section className="bg-ink px-5 py-24 text-sand sm:px-10">
        <div className="mx-auto grid max-w-[1440px] gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow text-amber">Drop in</p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-tight sm:text-5xl">Come as you are.</h2>
            <ul className="mt-8 space-y-5 text-sand/85">
              {["Have a cuppa and a yarn with community", "Talk with someone who understands culture and family", "Get connected to health, housing, legal and wellbeing services"].map((t) => (
                <li key={t} className="border-t border-sand/15 pt-5">{t}</li>
              ))}
            </ul>
            <div className="mt-10 space-y-2 text-sm text-sand/85">
              <p><span className="eyebrow text-amber">Where</span> — [Safe Place address]</p>
              <p><span className="eyebrow text-amber">When</span> — [Drop-in days and hours]</p>
            </div>
            <Link to="/programmes/safe-place" data-testid="safe-place-programme-link" className="btn-line mt-10 border-sand/40 text-sand hover:bg-sand hover:text-ink">Join the Safe Place Circle <ArrowUpRight className="h-4 w-4" /></Link>
          </Reveal>
          <Reveal delay={0.1} className="text-ink lg:col-span-6 lg:col-start-7">
            <p className="eyebrow mb-4 text-amber">Private support request</p>
            <SupportForm service={service} setService={setService} />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
